import React, { useCallback } from 'react';

import { Box, Text } from '@adsk/alloy-react-box';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';

import { useTranslation } from '../i18n';

const CAPACITY_ALMOST_REACHED_THRESHOLD = 3;

type UseCapacityAlertParams = {
  maxNumberOfAttachments: number | undefined;
};

type NotifyCapacityParams = {
  totalAttachmentsCount: number;
};

const useCapacityAlert = ({
  maxNumberOfAttachments,
}: UseCapacityAlertParams) => {
  const { showNotification } = useNotifications();
  const { t } = useTranslation();

  const notifyCapacityLimitIfNeeded = useCallback(
    ({ totalAttachmentsCount }: NotifyCapacityParams) => {
      if (!maxNumberOfAttachments) return;

      const remainingCapacity = maxNumberOfAttachments - totalAttachmentsCount;
      const isCapacityAlmostReached =
        remainingCapacity <= CAPACITY_ALMOST_REACHED_THRESHOLD;

      if (isCapacityAlmostReached) {
        showNotification({
          status: NOTIFICATION_STATUSES.WARNING,
          className: 'attachmentCapacityAlert',
          message: (
            <Box display="flex" flexDirection="column" justifyContent="center">
              <Text
                typography="heading4"
                data-testid="attachmentCapacityAlertText"
              >
                {t('MAX_ATTACHMENTS_CAPACITY_ALMOST_REACHED_TEXT', {
                  maxNumberOfAttachments,
                })}
              </Text>
              <Text
                typography="bodyMedium"
                data-testid="attachmentCapacityAlertSubText"
              >
                {t('MAX_ATTACHMENTS_CAPACITY_ALMOST_REACHED_SUBTEXT')}
              </Text>
            </Box>
          ),
        });
      }
    },
    [maxNumberOfAttachments, showNotification, t],
  );

  return { notifyCapacityLimitIfNeeded };
};

export default useCapacityAlert;
